import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtDate, fmtDateTime } from './UI';
import { useRollup, RollupH2 } from './Rollup';

// =====================================================================
// Ops read-only Requests & Estimates viewer (George 2026-08-11)
// =====================================================================
// "Operations needs read only access to the estimates and the sales
// requests. No margins, no summary information but she be able to see how
// the quote was put together."
//
// Renders /api/ops-sales — bill-side only by SERVER construction (the
// estimate detail is quotes.js buildCustomerView, the same view the quote
// PDF prints from; request lines pass the utils/opsSalesView.js whitelist).
// Nothing here is editable; there is no mutating endpoint to call.

const EST_STATUS_COLOR = {
  Draft:     { bg: '#FEF3C7', fg: '#92400E', border: '#F59E0B' },
  Submitted: { bg: '#DBEAFE', fg: '#1E40AF', border: '#3B82F6' },
  Approved:  { bg: '#D1FAE5', fg: '#065F46', border: '#10B981' },
  Rejected:  { bg: '#FEE2E2', fg: '#991B1B', border: '#EF4444' },
};
const REQ_STATUS_COLOR = {
  Draft:     EST_STATUS_COLOR.Draft,
  Submitted: EST_STATUS_COLOR.Submitted,
};
const GRAY = { bg: '#F3F4F6', fg: '#374151', border: '#D1D5DB' };
const PURPLE = { bg: '#EDE9FE', fg: '#5B21B6', border: '#8B5CF6' };

const money2 = (v) => (v === null || v === undefined || v === '' ? '—'
  : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const numOrDash = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-US'));

function Chip({ text, c }) {
  const cc = c || GRAY;
  return (
    <span style={{ background: cc.bg, color: cc.fg, border: `1px solid ${cc.border}`,
      borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

const sectionH = { fontSize: 14, fontWeight: 700, color: colors.charcoal, margin: '18px 0 8px' };
const readOnlyNote = {
  background: '#EFF6FF', border: '1px solid #93C5FD', color: '#1E40AF',
  borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12,
};
const draftNote = {
  background: '#FEF3C7', border: '1px solid #F59E0B', color: '#92400E',
  borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12, fontWeight: 600,
};
const coNote = {
  background: PURPLE.bg, border: `1px solid ${PURPLE.border}`, color: PURPLE.fg,
  borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12, fontWeight: 600,
};

// ===== Estimate detail — "how the quote was put together" ===============

function EstimateDetail({ id, onBack, onOpenRequest }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    setData(null); setError('');
    api.opsSalesEstimate(id).then(setData).catch(e => setError(e.message));
  }, [id]);
  // Rollups — before the early returns below (hooks must run every render).
  const [rLabor, tLabor] = useRollup('opssales-est-labor');
  const [rEquip, tEquip] = useRollup('opssales-est-equip');
  const [rCons, tCons] = useRollup('opssales-est-cons');
  const [rTotals, tTotals] = useRollup('opssales-est-totals');
  const [rTiers, tTiers] = useRollup('opssales-est-tiers');

  if (error) return <div style={styles.card}><div style={{ color: colors.red }}>{error}</div></div>;
  if (!data) return <div style={styles.card}>Loading…</div>;

  const { estimate: est, view, quote, request } = data;
  const label = `EST-${est.root_id}-${est.revision}`;
  const labor = view.labor || [];
  const equipment = view.equipment || [];
  const consumables = view.consumables || [];
  const empMob = view.employee_mobilization || [];
  const tiers = view.tiers || {};

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <button style={styles.btnSecondary} onClick={onBack}>← Back to list</button>
        <span style={{ fontSize: 17, fontWeight: 700, color: colors.charcoal }}>{label}</span>
        <Chip text={est.status} c={EST_STATUS_COLOR[est.status]} />
        {est.change_order_of_estimate_id != null && (
          <Chip text={`CHANGE ORDER · of EST-${est.change_order_of_estimate_id}`} c={PURPLE} />
        )}
        {quote && <Chip text={`${quote.label} · ${quote.status}`} c={GRAY} />}
        {request && (
          <button style={{ ...styles.btnSecondary, padding: '4px 10px', fontSize: 12 }}
            onClick={() => onOpenRequest(request.id)}>
            {request.label} →
          </button>
        )}
      </div>
      <div style={{ fontSize: 13, color: colors.textMuted || '#6B7280', marginBottom: 12 }}>
        {[est.customer_name, est.project_site, est.salesperson && `AM: ${est.salesperson}`]
          .filter(Boolean).join(' · ')}
      </div>

      <div style={readOnlyNote}>
        Read-only view. Amounts shown are what the customer is quoted — the same
        breakdown as the quote document.
      </div>
      {est.change_order_of_estimate_id != null && (
        <div style={coNote}>
          CHANGE ORDER — this prices additional scope billed IN ADDITION TO the
          base job (EST-{est.change_order_of_estimate_id} chain). It is not the
          whole job.
        </div>
      )}
      {est.status !== 'Approved' && (
        <div style={draftNote}>⚠ Pricing not yet approved (estimate {est.status}).</div>
      )}

      {labor.length > 0 && <>
        <RollupH2 as="div" collapsed={rLabor} onToggle={tLabor} style={sectionH}>Labor</RollupH2>
        {!rLabor && <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Positions</th>
              <th style={styles.th}>ST Hrs</th>
              <th style={styles.th}>OT Hrs</th>
              <th style={styles.th}>Bill ST</th>
              <th style={styles.th}>Bill OT</th>
              <th style={styles.th}>Per Diem</th>
              <th style={styles.th}>Total</th>
            </tr></thead>
            <tbody>
              {labor.map((l, i) => (
                <tr key={i}>
                  <td style={styles.td}>{l.craft}</td>
                  <td style={styles.td}>{numOrDash(l.qty_positions)}</td>
                  <td style={styles.td}>{numOrDash(l.st_hours)}</td>
                  <td style={styles.td}>{numOrDash(l.ot_hours)}</td>
                  <td style={styles.td}>{money2(l.bill_st)}</td>
                  <td style={styles.td}>{money2(l.bill_ot)}</td>
                  <td style={styles.td}>{l.per_diem_bill != null ? money2(l.per_diem_bill) + '/day' : '—'}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{l.rate_only ? 'rate only' : money2(l.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </>}

      {equipment.length > 0 && <>
        <RollupH2 as="div" collapsed={rEquip} onToggle={tEquip} style={sectionH}>Equipment</RollupH2>
        {!rEquip && <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Qty</th>
              <th style={styles.th}>Days</th>
              <th style={styles.th}>Usage Rate</th>
              <th style={styles.th}>Total</th>
            </tr></thead>
            <tbody>
              {equipment.map((e, i) => (
                <tr key={i}>
                  <td style={styles.td}>{e.description}</td>
                  <td style={styles.td}>{e.equip_type}{e.metered ? ' (metered)' : ''}</td>
                  <td style={styles.td}>{numOrDash(e.qty)}</td>
                  <td style={styles.td}>{numOrDash(e.num_days)}</td>
                  <td style={styles.td}>{money2(e.day_rate)}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    {money2(e.revenue)}{e.metered ? ' @100%' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </>}

      {(consumables.length > 0 || empMob.length > 0) && <>
        <RollupH2 as="div" collapsed={rCons} onToggle={tCons} style={sectionH}>Consumables, Services and Charges</RollupH2>
        {!rCons && <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Qty</th>
              <th style={styles.th}>Rate</th>
              <th style={styles.th}>Total</th>
            </tr></thead>
            <tbody>
              {consumables.concat(empMob.map(m => ({ ...m, _mob: true }))).map((c, i) => (
                <tr key={i}>
                  <td style={styles.td}>{c._mob ? `Employee Mobilization — ${c.description}` : c.description}</td>
                  <td style={styles.td}>{numOrDash(c.qty)}</td>
                  <td style={styles.td}>{money2(c.bill_rate)}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{money2(c.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </>}

      <RollupH2 as="div" collapsed={rTotals} onToggle={tTotals} style={sectionH}>Quote Totals</RollupH2>
      {!rTotals && <div style={{ overflowX: 'auto', maxWidth: 460 }}>
        <table style={styles.table}>
          <tbody>
            {(view.rollup || []).map((r, i) => (
              <tr key={i}>
                <td style={styles.td}>{r.category}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{money2(r.revenue)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...styles.td, fontWeight: 700 }}>
                TOTAL{tiers.has_utilization ? ` (at ${est.utilization_tier ?? 70}% utilization)` : ''}
              </td>
              <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>{money2(view.total_revenue)}</td>
            </tr>
          </tbody>
        </table>
      </div>}

      {/* Priced AT 100% utilization = no 50/70 scenarios exist — the quote
          shows one total, so this window onto it does too (George 2026-08-19). */}
      {tiers.has_utilization && !tiers.single_100 && (
        <>
          <RollupH2 as="div" collapsed={rTiers} onToggle={tTiers} style={sectionH}>Utilization Tier Scenarios</RollupH2>
          {!rTiers && <div style={{ overflowX: 'auto', maxWidth: 620 }}>
            <table style={styles.table}>
              <thead><tr>
                <th style={styles.th}></th>
                <th style={styles.th}>50%</th>
                <th style={styles.th}>70%</th>
                <th style={styles.th}>100%</th>
              </tr></thead>
              <tbody>
                <tr>
                  <td style={styles.td}>Utilization equipment</td>
                  <td style={styles.td}>{money2(tiers.util50)}</td>
                  <td style={styles.td}>{money2(tiers.util70)}</td>
                  <td style={styles.td}>{money2(tiers.util100)}</td>
                </tr>
                <tr>
                  <td style={{ ...styles.td, fontWeight: 600 }}>Project total</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{money2(tiers.total50)}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{money2(tiers.total70)}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{money2(tiers.total100)}</td>
                </tr>
              </tbody>
            </table>
          </div>}
        </>
      )}
    </div>
  );
}

// ===== Request detail — questionnaire + target lines =====================

function phaseSummary(p) {
  const dates = p.start_date ? `${p.start_date} → ${p.end_date || '?'}` : 'undated';
  const hc = `${p.day_hc || 0}D/${p.night_hc || 0}N`;
  return `${p.phase}: ${dates} · ${p.hours_per_day || '—'}h/d · ${hc}`;
}

function RequestDetail({ id, onBack, onOpenEstimate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    setData(null); setError('');
    api.opsSalesRequest(id).then(setData).catch(e => setError(e.message));
  }, [id]);
  // Rollups — before the early returns below (hooks must run every render).
  const [rLabor, tLabor] = useRollup('opssales-req-labor');
  const [rEquip, tEquip] = useRollup('opssales-req-equip');
  const [rCons, tCons] = useRollup('opssales-req-cons');
  const [rQs, tQs] = useRollup('opssales-req-questionnaire');

  if (error) return <div style={styles.card}><div style={{ color: colors.red }}>{error}</div></div>;
  if (!data) return <div style={styles.card}>Loading…</div>;

  const { request: r, scope_labels, answers, labor_lines, equipment_lines, consumable_lines } = data;
  const label = `REQ-${r.root_id}-${r.revision}`;

  // Group answered questions by section, preserving server order
  // (Conclusion is already sorted last server-side).
  const sections = [];
  for (const a of answers) {
    const last = sections[sections.length - 1];
    if (last && last.section === a.section) last.rows.push(a);
    else sections.push({ section: a.section, rows: [a] });
  }

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <button style={styles.btnSecondary} onClick={onBack}>← Back to list</button>
        <span style={{ fontSize: 17, fontWeight: 700, color: colors.charcoal }}>{label}</span>
        <Chip text={r.status} c={REQ_STATUS_COLOR[r.status]} />
        {!r.is_current && <Chip text="Superseded" c={GRAY} />}
        {r.estimate_id && (
          <button style={{ ...styles.btnSecondary, padding: '4px 10px', fontSize: 12 }}
            onClick={() => onOpenEstimate(r.estimate_id)}>
            View estimate →
          </button>
        )}
      </div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 6 }}>
        {[r.title, r.customer_name, r.project_site, r.created_by_name && `AM: ${r.created_by_name}`]
          .filter(Boolean).join(' · ')}
      </div>
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
        {r.submitted_at ? `Submitted ${fmtDateTime(r.submitted_at)}` : 'Not yet submitted'}
        {r.quote_due_by ? ` · Quote due ${fmtDate(r.quote_due_by)}` : ''}
      </div>

      <div style={readOnlyNote}>
        Read-only view. Rates shown are the account manager's target BILL rates —
        what sales asked to charge the customer.
      </div>

      {scope_labels.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {scope_labels.map((s, i) => <Chip key={i} text={s} c={GRAY} />)}
        </div>
      )}

      {labor_lines.length > 0 && <>
        <RollupH2 as="div" collapsed={rLabor} onToggle={tLabor} style={sectionH}>Labor (target)</RollupH2>
        {!rLabor && <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Craft</th>
              <th style={styles.th}>Positions</th>
              <th style={styles.th}>Shift Hrs</th>
              <th style={styles.th}>Target Bill ST</th>
              <th style={styles.th}>Target Bill OT</th>
              <th style={styles.th}>Per Diem</th>
              <th style={styles.th}>Phases</th>
            </tr></thead>
            <tbody>
              {labor_lines.map((l, i) => (
                <tr key={i}>
                  <td style={styles.td}>
                    {l.msa_description || l.craft}{l.rate_only ? ' (rate only)' : ''}
                    {l.labor_type !== 'Standard' && <span style={{ color: '#6B7280' }}> · {l.labor_type}</span>}
                  </td>
                  <td style={styles.td}>{numOrDash(l.qty_positions)}</td>
                  <td style={styles.td}>{numOrDash(l.shift_hours)}</td>
                  <td style={styles.td}>{money2(l.target_bill_st)}</td>
                  <td style={styles.td}>{money2(l.target_bill_ot)}</td>
                  <td style={styles.td}>
                    {l.per_diem_eligible ? (l.target_per_diem_bill != null ? money2(l.target_per_diem_bill) + '/day' : 'eligible') : '—'}
                  </td>
                  <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}>
                    {(l.phases || []).filter(p => p.start_date || p.day_hc || p.night_hc)
                      .map((p, j) => <div key={j}>{phaseSummary(p)}</div>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </>}

      {equipment_lines.length > 0 && <>
        <RollupH2 as="div" collapsed={rEquip} onToggle={tEquip} style={sectionH}>Equipment (target)</RollupH2>
        {!rEquip && <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>CAT-CLASS</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Qty</th>
              <th style={styles.th}>Days</th>
              <th style={styles.th}>Dates</th>
              <th style={styles.th}>Target Usage Rate</th>
            </tr></thead>
            <tbody>
              {equipment_lines.map((e, i) => (
                <tr key={i}>
                  <td style={styles.td}>{e.msa_description || e.description}</td>
                  <td style={styles.td}>{e.cat_class || '—'}</td>
                  <td style={styles.td}>{e.equip_type}</td>
                  <td style={styles.td}>{numOrDash(e.qty)}</td>
                  <td style={styles.td}>{numOrDash(e.num_days)}</td>
                  <td style={styles.td}>{e.start_date ? `${e.start_date} → ${e.end_date || '?'}` : '—'}</td>
                  <td style={styles.td}>{money2(e.target_day_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </>}

      {consumable_lines.length > 0 && <>
        <RollupH2 as="div" collapsed={rCons} onToggle={tCons} style={sectionH}>Consumables, Services and Charges (target)</RollupH2>
        {!rCons && <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Part #</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Qty</th>
              <th style={styles.th}>Basis</th>
              <th style={styles.th}>Target Bill Rate</th>
            </tr></thead>
            <tbody>
              {consumable_lines.map((c, i) => (
                <tr key={i}>
                  <td style={styles.td}>{c.description}</td>
                  <td style={styles.td}>{c.part_number || '—'}</td>
                  <td style={styles.td}>{c.category || '—'}</td>
                  <td style={styles.td}>{numOrDash(c.qty)}{c.unit ? ` ${c.unit}` : ''}</td>
                  <td style={styles.td}>{c.billing_basis || '—'}</td>
                  <td style={styles.td}>{money2(c.target_bill_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </>}

      {sections.length > 0 && <>
        <RollupH2 as="div" collapsed={rQs} onToggle={tQs} style={sectionH}>Questionnaire (answered)</RollupH2>
        {!rQs && sections.map((s, i) => (
          <div key={i} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.orange, textTransform: 'uppercase', margin: '8px 0 4px' }}>
              {s.section}
            </div>
            {s.rows.map((a, j) => (
              <div key={j} style={{ padding: '6px 0', borderBottom: '1px solid #F3F4F6', fontSize: 13 }}>
                <div style={{ color: '#6B7280' }}>{a.question_n}. {a.question}</div>
                <div style={{ color: colors.charcoal, whiteSpace: 'pre-wrap' }}>{a.response}</div>
                {a.follow_up && (
                  <div style={{ color: '#92400E', fontSize: 12, whiteSpace: 'pre-wrap' }}>↳ {a.follow_up}</div>
                )}
              </div>
            ))}
          </div>
        ))}
      </>}
    </div>
  );
}

// ===== Lists + mode toggle ==============================================

export default function OpsSalesView() {
  const [mode, setMode] = useState('estimates');           // 'estimates' | 'requests'
  const [openEstimate, setOpenEstimate] = useState(null);  // estimate id
  const [openRequest, setOpenRequest] = useState(null);    // request id
  const [estimates, setEstimates] = useState(null);
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    api.opsSalesEstimates().then(setEstimates).catch(e => setError(e.message));
    api.opsSalesRequests().then(setRequests).catch(e => setError(e.message));
  }, []);

  const list = mode === 'estimates' ? estimates : requests;
  const filtered = useMemo(() => {
    if (!list) return null;
    const q = search.trim().toLowerCase();
    return list.filter(row => {
      if (statusFilter && row.status !== statusFilter) return false;
      if (!q) return true;
      return [row.customer_name, row.project_site, row.title, row.salesperson, row.created_by_name]
        .some(v => v && String(v).toLowerCase().includes(q));
    });
  }, [list, search, statusFilter]);

  const statuses = mode === 'estimates' ? ['Draft', 'Submitted', 'Approved', 'Rejected'] : ['Draft', 'Submitted'];

  if (openEstimate) {
    return <EstimateDetail id={openEstimate} onBack={() => setOpenEstimate(null)}
      onOpenRequest={(rid) => { setOpenEstimate(null); setOpenRequest(rid); }} />;
  }
  if (openRequest) {
    return <RequestDetail id={openRequest} onBack={() => setOpenRequest(null)}
      onOpenEstimate={(eid) => { setOpenRequest(null); setOpenEstimate(eid); }} />;
  }

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        {[['estimates', '📗 Estimates'], ['requests', '🧾 Sales Requests']].map(([v, l]) => (
          <button key={v} style={mode === v ? styles.tabBtnActive : styles.tabBtn}
            onClick={() => { setMode(v); setStatusFilter(''); }}>{l}</button>
        ))}
        <input
          placeholder="Search customer / site / AM…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 10px', border: `1px solid ${colors.border || '#D1D5DB'}`, borderRadius: 8, fontSize: 13, minWidth: 240 }}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '8px 10px', border: `1px solid ${colors.border || '#D1D5DB'}`, borderRadius: 8, fontSize: 13 }}>
          <option value="">All statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {filtered && <span style={{ fontSize: 12, color: '#6B7280' }}>{filtered.length} of {list.length}</span>}
      </div>

      <div style={readOnlyNote}>
        Read-only. Bill-side pricing only — what the customer is quoted. No cost,
        pay, or margin data appears anywhere in this view.
      </div>

      {error && <div style={{ color: colors.red, marginBottom: 10 }}>{error}</div>}
      {!filtered && !error && <div>Loading…</div>}

      {filtered && mode === 'estimates' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Estimate</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Site / Deal</th>
              <th style={styles.th}>AM</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Start</th>
              <th style={styles.th}>Updated</th>
            </tr></thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => setOpenEstimate(e.id)}>
                  <td style={{ ...styles.td, fontWeight: 600, color: colors.orange }}>
                    EST-{e.root_id}-{e.revision}
                    {e.change_order_of_estimate_id != null && (
                      <span title={`Change order of the EST-${e.change_order_of_estimate_id} chain — bills on top of the base job`}
                        style={{ background: PURPLE.bg, color: PURPLE.fg, fontWeight: 700, fontSize: 9,
                          padding: '1px 5px', borderRadius: 4, marginLeft: 5, whiteSpace: 'nowrap' }}>
                        CO
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>{e.customer_name || '—'}</td>
                  <td style={styles.td}>{e.project_site || '—'}</td>
                  <td style={styles.td}>{e.salesperson || e.created_by_name || '—'}</td>
                  <td style={styles.td}><Chip text={e.status} c={EST_STATUS_COLOR[e.status]} /></td>
                  <td style={styles.td}>{e.start_date ? fmtDate(e.start_date) : '—'}</td>
                  <td style={styles.td}>{fmtDate(e.updated_at)}</td>
                </tr>
              ))}
              {!filtered.length && <tr><td style={styles.td} colSpan={7}>No estimates match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {filtered && mode === 'requests' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Request</th>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>AM</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Submitted</th>
            </tr></thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setOpenRequest(r.id)}>
                  <td style={{ ...styles.td, fontWeight: 600, color: colors.orange }}>REQ-{r.root_id}-{r.revision}</td>
                  <td style={styles.td}>{r.title || '—'}</td>
                  <td style={styles.td}>{r.customer_name || '—'}</td>
                  <td style={styles.td}>{r.created_by_name || '—'}</td>
                  <td style={styles.td}><Chip text={r.status} c={REQ_STATUS_COLOR[r.status]} /></td>
                  <td style={styles.td}>{r.submitted_at ? fmtDate(r.submitted_at) : '—'}</td>
                </tr>
              ))}
              {!filtered.length && <tr><td style={styles.td} colSpan={6}>No requests match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
